import React, { createContext, useContext } from "react";

interface ApiRequestOptions {
  api?: 'public' | 'contact';
  returnResponse?: boolean; // only used for POST
}

interface ApiContextType {
  get: (path: string, options?: ApiRequestOptions) => Promise<any>;
  post: (path: string, data: any, options?: ApiRequestOptions | boolean) => Promise<any>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

const ensureTrailingSlash = (s: string) => (s.endsWith('/') ? s : s + '/');

const getApiBases = () => {
  const hostname = window.location.hostname;
  const isSandbox = hostname === 'localhost' || hostname.includes('sandbox');

  if (isSandbox) {
    return {
      publicBase: ensureTrailingSlash('https://i685jy1v76.execute-api.eu-west-1.amazonaws.com/v1'),
      contactBase: ensureTrailingSlash('https://ozprwtooq8.execute-api.eu-west-1.amazonaws.com/v1'),
    };
  }
  return {
    publicBase: ensureTrailingSlash('https://5wfi5khqwh.execute-api.eu-west-1.amazonaws.com/v1'),
    contactBase: ensureTrailingSlash('https://o3s96v94ke.execute-api.eu-west-1.amazonaws.com/v1'),
  };
};

const apiBases = getApiBases();
const resolveBase = (api?: 'public' | 'contact') => (api === 'contact' ? apiBases.contactBase : apiBases.publicBase);
const joinUrl = (base: string, path: string) => base + (path.startsWith('/') ? path.substring(1) : path);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const get = async (path: string, options?: ApiRequestOptions) => {
    const base = resolveBase(options?.api);
    const response = await fetch(joinUrl(base, path), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  };

  const post = async (path: string, data: any, options?: ApiRequestOptions | boolean) => {
    const opts: ApiRequestOptions = typeof options === 'boolean' ? { returnResponse: options } : (options || {});
    const base = resolveBase(opts.api);

    const response = await fetch(joinUrl(base, path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return opts.returnResponse ? response : response.json();
  };

  return (
    <ApiContext.Provider value={{ get, post }}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};